/**
 * TanStack Query hooks for the portal endpoints.
 *
 * Conventions:
 *   - Read endpoints use `useQuery` with stable `queryKey` shapes.
 *   - Write endpoints (login, intent creation) use `useMutation` so they
 *     don't refetch automatically — the page composes them imperatively.
 *   - We don't invalidate `paymentMe` on payment success because the
 *     handoff §4 calls out that the webhook posts the ledger update
 *     asynchronously; an immediate refetch would just show stale data.
 *     Instead, the success page renders from navigation-state and the
 *     next mount of `/pay` will see the updated balance.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import {
  authenticate,
  createBalanceIntent,
  createPrecollectIntent,
  fetchCheckinBalance,
  fetchPaymentMe,
  logout,
  verifyMagicLink,
} from './index';
import type {
  AuthenticateRequest,
  AuthenticateResponse,
  CreateBalanceIntentRequest,
  CreatePaymentIntentResponse,
  CreatePrecollectIntentRequest,
  FetchCheckinBalanceResponse,
  FetchPaymentMeResponse,
  MagicLinkRequest,
  MagicLinkResponse,
} from './types';
import { storeAuth, clearAuth } from './storage';

// ---------- Query keys -----------------------------------------------------

export const queryKeys = {
  paymentMe: () => ['portal', 'payment', 'me'] as const,
  checkinBalance: (appointmentId: number) =>
    ['portal', 'checkin', 'balance', appointmentId] as const,
};

// ---------- Read hooks -----------------------------------------------------

export function usePaymentMe(
  options?: Omit<UseQueryOptions<FetchPaymentMeResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FetchPaymentMeResponse>({
    queryKey: queryKeys.paymentMe(),
    queryFn: fetchPaymentMe,
    staleTime: 30_000,
    ...options,
  });
}

export function useCheckinBalance(
  appointmentId: number | null,
  options?: Omit<
    UseQueryOptions<FetchCheckinBalanceResponse>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<FetchCheckinBalanceResponse>({
    queryKey: queryKeys.checkinBalance(appointmentId ?? -1),
    queryFn: () => fetchCheckinBalance(appointmentId as number),
    enabled: appointmentId !== null,
    staleTime: 30_000,
    ...options,
  });
}

// ---------- Write hooks ----------------------------------------------------

/**
 * Login mutation. On success, persists the JWT to sessionStorage. The page
 * is responsible for navigation (different success destinations depending
 * on the `?next=` param).
 */
export function useAuthenticate() {
  return useMutation<AuthenticateResponse, Error, AuthenticateRequest>({
    mutationFn: authenticate,
    onSuccess: (data) => {
      storeAuth(data);
    },
  });
}

/**
 * Magic-link verify mutation. On success, persists the JWT + redirect_path
 * to sessionStorage via `storeAuth`. The page is responsible for navigation
 * — it reads `data.redirect_path` from the success response and routes
 * there (does NOT decode the JWT, per ledger #11 + #42).
 *
 * Per ledger #41: this mutation is the only place the token is consumed.
 * Callers must NOT trigger it on mount (GET / link-preview-bot path);
 * only on explicit user submit (POST after DOB entry).
 */
export function useVerifyMagicLink() {
  return useMutation<MagicLinkResponse, Error, MagicLinkRequest>({
    mutationFn: verifyMagicLink,
    onSuccess: (data) => {
      storeAuth(data);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      try {
        await logout();
      } catch {
        // Best-effort — see §7. Always clear storage even on network failure.
      }
      clearAuth();
      sessionStorage.removeItem('justPaidVisits');
      // Per-appointment map (same-day Phase 1) plus the pre-Phase-1
      // single-number key, in case a tab still carries the old one.
      sessionStorage.removeItem('justPaidEstimates');
      sessionStorage.removeItem('justPaidEstimate');
      sessionStorage.removeItem('justPaidPatientId');
      // Drop every cached query. Read keys carry no patient identifier
      // (queryKeys.paymentMe() is the static ['portal','payment','me']),
      // so anything left in the cache is handed straight to the NEXT
      // patient who logs in on this tab — their receipt-email field
      // would pre-fill with the previous patient's address.
      queryClient.clear();
    },
  });
}

export function useCreateBalanceIntent() {
  return useMutation<
    CreatePaymentIntentResponse,
    Error,
    CreateBalanceIntentRequest
  >({
    mutationFn: createBalanceIntent,
  });
}

export function useCreatePrecollectIntent() {
  return useMutation<
    CreatePaymentIntentResponse,
    Error,
    CreatePrecollectIntentRequest
  >({
    mutationFn: createPrecollectIntent,
  });
}
